import * as yup from 'yup';
import { FieldConfig } from './types';

export const generateSchema = (fields: FieldConfig[]) => {
  const schemaFields: Record<string, any> = {};

  fields.forEach((field) => {
    let fieldSchema: any;

    switch (field.type) {
      case 'text':
      case 'textarea':
      case 'tel':
        fieldSchema = yup.string();
        if (field.validation) {
          fieldSchema = applyCustomValidation(fieldSchema, field, 'string');
        } else {
          if (field.minLength) {
            fieldSchema = fieldSchema.min(field.minLength, `${field.label} must be at least ${field.minLength} characters`);
          }
          if (field.maxLength) {
            fieldSchema = fieldSchema.max(field.maxLength, `${field.label} must be at most ${field.maxLength} characters`);
          }
          if (field.pattern) {
            fieldSchema = fieldSchema.test('pattern', `${field.label} format is invalid`, (value: any) => {
              if (!value) return true; // Allow empty values unless required
              return new RegExp(field.pattern!).test(value);
            });
          }
        }
        break;

      case 'email':
        fieldSchema = yup.string().email(field.validation?.message || `${field.label} must be a valid email address`);
        if (field.validation) {
          fieldSchema = applyCustomValidation(fieldSchema, field, 'string');
        }
        break;

      case 'password':
        fieldSchema = yup.string();
        if (field.validation) {
          fieldSchema = applyCustomValidation(fieldSchema, field, 'string');
        } else {
          if (field.minLength) {
            fieldSchema = fieldSchema.min(field.minLength, `${field.label} must be at least ${field.minLength} characters`);
          }
          if (field.maxLength) {
            fieldSchema = fieldSchema.max(field.maxLength, `${field.label} must be at most ${field.maxLength} characters`);
          }
          if (field.pattern) {
            fieldSchema = fieldSchema.test('pattern', `${field.label} format is invalid`, (value: any) => {
              if (!value) return true; // Allow empty values unless required
              return new RegExp(field.pattern!).test(value);
            });
          }
        }
        break;

      case 'number':
        fieldSchema = yup.number().typeError(`${field.label} must be a number`);
        if (field.validation) {
          fieldSchema = applyCustomValidation(fieldSchema, field, 'number');
        } else {
          if (field.min !== undefined) {
            fieldSchema = fieldSchema.min(field.min, `${field.label} must be at least ${field.min}`);
          }
          if (field.max !== undefined) {
            fieldSchema = fieldSchema.max(field.max, `${field.label} must be at most ${field.max}`);
          }
        }
        break;

      case 'select':
        fieldSchema = yup.string();
        if (field.validation) {
          fieldSchema = applyCustomValidation(fieldSchema, field, 'string');
        }
        break;

      case 'checkbox':
        fieldSchema = yup.boolean();
        if (field.validation) {
          fieldSchema = applyCustomValidation(fieldSchema, field, 'boolean');
        }
        break;

      case 'date':
        fieldSchema = yup.date().typeError(`${field.label} must be a valid date`);
        if (field.validation) {
          fieldSchema = applyCustomValidation(fieldSchema, field, 'date');
        } else {
          if (field.minDate) {
            fieldSchema = fieldSchema.min(new Date(field.minDate), `${field.label} must be after ${field.minDate}`);
          }
          if (field.maxDate) {
            fieldSchema = fieldSchema.max(new Date(field.maxDate), `${field.label} must be before ${field.maxDate}`);
          }
        }
        break;

      default:
        fieldSchema = yup.mixed();
    }

    const isRequired = field.validation?.required !== undefined ? field.validation.required : field.required;
    if (isRequired) {
      const requiredMessage = field.validation?.message || `${field.label} is required`;
      fieldSchema = fieldSchema.required(requiredMessage);
    }

    schemaFields[field.name] = fieldSchema;
  });

  return yup.object().shape(schemaFields);
};

const applyCustomValidation = (schema: any, field: FieldConfig, fieldType: string) => {
  const validation = field.validation;
  if (!validation) return schema;

  if (fieldType === 'string') {
    if (validation.minLength !== undefined) {
      const message = validation.message || `${field.label} must be at least ${validation.minLength} characters`;
      schema = schema.min(validation.minLength, message);
    }
    if (validation.maxLength !== undefined) {
      const message = validation.message || `${field.label} must be at most ${validation.maxLength} characters`;
      schema = schema.max(validation.maxLength, message);
    }
  }

  if (fieldType === 'number') {
    if (validation.min !== undefined) {
      const message = validation.message || `${field.label} must be at least ${validation.min}`;
      schema = schema.min(validation.min, message);
    }
    if (validation.max !== undefined) {
      const message = validation.message || `${field.label} must be at most ${validation.max}`;
      schema = schema.max(validation.max, message);
    }
  }

  if (validation.pattern) {
    const pattern = typeof validation.pattern === 'string' ? new RegExp(validation.pattern) : validation.pattern;
    const message = validation.message || `${field.label} format is invalid`;
    schema = schema.test('pattern', message, (value: any) => {
      if (!value) return true; // Allow empty values unless required
      return pattern.test(value);
    });
  }

  if (validation.custom) {
    schema = schema.test('custom', validation.message || `${field.label} is invalid`, (value: any) => {
      const result = validation.custom!(value);
      return typeof result === 'boolean' ? result : false;
    });
  }

  return schema;
}; 